import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import api from '../services/api';
import { toast } from 'sonner';

const CATEGORIES = ['Restaurant', 'Grocery', 'Pharmacy', 'Cafe', 'Bakery', 'Florist', 'Retail', 'Services'];
const EXAMPLES = [
  "Family-owned West African restaurant in Brooklyn — jollof rice, suya, egusi soup, plantains, palm wine. Average ticket $15-25.",
  "Specialty Italian bakery in Boston. Cannoli, biscotti, focaccia, sourdough, and cakes for parties.",
  "Small-batch craft cocktail bar in Austin — old fashioneds, mezcal flights, tapas-style small plates.",
  "Neighborhood pharmacy in Lagos — OTC meds, vitamins, baby care, beauty products. Same-day delivery.",
];

const VendorDescribeBusiness = () => {
  const nav = useNavigate();
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Restaurant');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const text = description.trim();
    if (text.length < 12) {
      toast.error('Tell us a bit more — at least one sentence about your business.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/onboarding/describe', { description: text, category });
      const n = data.imported_items || 0;
      const secs = data.imported_sections || 0;
      if (n > 0) {
        toast.success(`Created ${data.vendor.name} with ${n} items across ${secs} sections. Review prices below.`);
      } else {
        toast.info("Created your store — add a few items to get started.");
      }
      nav(data.redirect || '/vendor-admin/menu');
      setTimeout(() => window.location.reload(), 50);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not create store';
      toast.error(msg, { duration: 6000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Wand2 className="h-5 w-5 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">Describe your business</h2>
      </div>
      <p className="text-sm text-slate-500">
        Tell us what you sell, who your customers are, and where you're located. Our AI will invent a complete
        starter catalog — sections, items, realistic prices — that you can refine in seconds.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
        <div>
          <Label className="text-xs text-slate-600">Tell us about your business</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. We run a family-owned Italian bakery in Ottawa — fresh cannoli, focaccia, sourdough loaves, and custom cakes for parties. Mostly takeout & delivery."
            rows={5}
            className="mt-1 resize-none"
            data-testid="describe-textarea"
            maxLength={2000}
          />
          <div className="mt-1 text-[11px] text-slate-400 flex items-center justify-between">
            <span>{description.length}/2000 characters</span>
            <span>Tip: mention your city, top products, and price range.</span>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Category</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="describe-category-select"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={busy}
        className="mt-4 h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white"
        data-testid="describe-go-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
        {busy ? 'Building your store…' : 'Generate my store with AI'}
      </Button>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Need inspiration?</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {EXAMPLES.map((ex, i) => (
            <button
              key={i}
              onClick={() => setDescription(ex)}
              data-testid={`describe-example-${i}`}
              className="text-left text-[12px] leading-snug px-2.5 py-2 max-w-xs rounded-lg bg-slate-50 hover:bg-emerald-50 hover:text-emerald-900 text-slate-600 border border-slate-100 hover:border-emerald-200 transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VendorDescribeBusiness;
